// @ts-nocheck
import { BreakpointType, hiddenStyles } from "@store/Themes/common/mixins/common";
import { contentSpacingStyles } from "@store/Themes/common/mixins/spacing";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductImageStyles } from "@store/Overrides/Components/ProductImage";
import TrashX from "@store/Library/Components/Icons/TrashX";
import { ProductAvailabilityStyles } from "@store/Overrides/Components/ProductAvailability";
import { ProductPriceStyles } from "@store/Overrides/Components/ProductPrice";
import { CartLineQuantityStyles } from "@store/Overrides/Widgets/Cart/CartLineQuantity";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { css } from "styled-components";

export interface StoreProductLineProps {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    productGridItem?: GridItemProps;
    productControlsGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    productInfosWrapper?: InjectableCss;
    productControls?: InjectableCss;
    productControlsItem?: InjectableCss;
    productDescription?: ProductDescriptionStyles;
    productPrice?: ProductPriceStyles;
    extendedUnitNetPriceText?: TypographyPresentationProps;
    quantityOrdered?: TextFieldProps;
    mobileProductControls?: InjectableCss;
    removeProductLineIcon?: IconPresentationProps;
    removeProductButton?: ClickablePresentationProps;
    productLineProductWidth?: GridWidths | GridWidths[];
    productLineControlsWidth?: GridWidths | GridWidths[];
    productAvailability?: ProductAvailabilityStyles;
    quantity?: CartLineQuantityStyles;
    notesWrapper?: InjectableCss;
    // bunzl custom Code
    mobileProductControlsItem: InjectableCss;
    addToCartAndAddToListRow?: InjectableCss;
}

export const productLineProps: StoreProductLineProps = {
    wrapper: {
        css: css`
            display: flex;
            padding: 1rem 0;
            border-bottom: ${pxToRem(1)} solid ${getColor("common.border")};

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            align-items: flex-start;
                        `,
                        null,
                        null,
                        css`
                            align-items: center;
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    container: {
        gap: 20,
        offsetCss: css`
            width: 100% !important; /* Need this to fix the variable width of the gridOffset */
        `,
    },
    productGridItem: {
        width: [12, 12, 12, 5, 5],
        align: "top",
        css: css`
            @media print {
                width: 100% !important;
                display: flex !important;
            }
        `,
    },
    productControlsGridItem: {
        width: 7,
        align: "middle",
        css: css`
            ${hiddenStyles(BreakpointType.Below, "md")}

            @media print {
                display: none !important;
            }
        `,
    },
    mobileProductControls: {
        css: css`
            width: 100%;
            display: grid;
            row-gap: ${({ theme }) => theme.design.layout.spacing.smaller};
            ${hiddenStyles(BreakpointType.Above, "lg")}
        `,
    },
    productControls: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },
    productControlsItem: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin-right: ${pxToRem(10)};
                                max-width: ${pxToRem(90)};
                                min-width: ${pxToRem(90)};
                            `,
                            css`
                                margin-right: ${pxToRem(30)};
                            `,
                            css`
                                margin-right: ${pxToRem(35)};
                                max-width: ${pxToRem(115)};
                                min-width: ${pxToRem(115)};
                            `,
                        ],
                        mediaOptions.min,
                    )}
                max-height: ${pxToRem(96)};
            `,
        },
        wrapper: {
            css: css`
                display: grid;
                height: 100%;
                width: auto;
            `,
        },
    },
    productInfosWrapper: {
        css: css`
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            row-gap: ${({ theme }) => theme.design.layout.spacing.smaller};
        `,
    },
    productDescription: {
        productDetailLink: {
            color: "text.main",
            hoverMode: "darken",
            css: css`
                font-weight: ${({ theme }) => theme.design.typography.weight.bold};
            `,
        },
        productDetailDescription: {
            color: "primary",
            css: css``,
        },
    },
    productPrice: {
        wrapper: {
            css: css`
                padding: 0;
                margin: 0;
            `,
        },
        price: {
            priceText: {
                color: "text.dark",
                css: css`
                    font-size: ${({ theme }) => theme.design.typography.size.base};
                    font-weight: ${({ theme }) => theme.design.typography.weight.bold};
                `,
            },
        },
    },
    extendedUnitNetPriceText: {
        color: "text.main",
        css: css`
            font-size: ${({ theme }) => theme.design.typography.size.base};
            font-weight: ${({ theme }) => theme.design.typography.weight.bold};
        `,
    },
    quantityOrdered: {
        css: css`
            text-align: center;
        `,
        cssOverrides: {
            formInputWrapper: css`
                max-width: ${pxToRem(65)};
            `,
            formField: css`
                margin-right: ${pxToRem(25)};
                max-width: ${pxToRem(65)};
            `,
        },
    },
    removeProductLineIcon: {
        src: TrashX,
        size: 24,
        color: "primary",
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;

            transition: ${transitionHoverStyles("color")};

            &:hover {
                color: ${getColor("text.linkHover")};
            }
        `,
    },
    removeProductButton: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        margin: auto 0;
                    `,
                    css`
                        margin: auto 0;
                    `,
                ])}
        `,
    },
    productLineProductWidth: [12, 12, 12, 6, 6],
    productLineControlsWidth: 6,
    productAvailability: {
        messageGridItem: {
            css: css`
                ${breakpointValues([".5em", null, null, "0"], "padding-top")}
            `,
        },
        messageText: {
            css: css`
                ${breakpointValues(["nowrap", null, null, "normal"], ["white-space"])}
            `,
        },
        inventoryMessage: {
            css: css`
                margin-left: 0;
            `,
        },
        wrapper: {
            css: css`
                max-width: 90%;
            `,
        },
    },
    notesWrapper: {
        css: css`
            width: 100%;
            ${contentSpacingStyles(pxToRem(12), null, null)}
        `,
    },
    mobileProductControlsItem: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },
    addToCartAndAddToListRow: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            ${breakpointValues(["normal", null, null, "space-between"], "justify-content")}
            ${breakpointValues([pxToRem(20), null, null, 0], "gap")};
        `,
    },
};
